import React, { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Loader2, CreditCard, Receipt, Wifi, WifiOff } from 'lucide-react';

export default function Pos() {
  const [form, setForm] = useState({ amount: '', currency: 'USD', description: '', reference: '' });
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);
  const [charges, setCharges] = useState([]);
  const [connected, setConnected] = useState(false);

  const loadCharges = async () => {
    try {
      const res = await api.get('/cyclos/pos/charges');
      setCharges(res.data.charges || []);
      setConnected(!!res.data.cyclos_connected);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => { loadCharges(); }, []);

  const createCharge = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Enter an amount greater than 0'); return; }
    setCreating(true);
    try {
      const res = await api.post('/cyclos/pos/charge', {
        amount: amt,
        currency: form.currency,
        description: form.description,
        reference: form.reference,
      });
      setActive(res.data);
      setForm((p) => ({ ...p, amount: '', description: '', reference: '' }));
      toast.success('Charge created — show the QR to your customer');
      loadCharges();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create charge');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-10" data-testid="pos-page">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-primary flex items-center gap-3">
              <CreditCard className="w-9 h-9 text-accent" /> EzyTransfer POS
            </h1>
            <p className="text-muted-foreground mt-2 text-base">Accept payments in-store — generate a scannable charge for any amount.</p>
          </div>
          {connected
            ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1" data-testid="pos-connected-badge"><Wifi className="w-3 h-3" /> Cyclos live</Badge>
            : <Badge className="bg-amber-50 text-amber-800 border border-amber-200 gap-1" data-testid="pos-connected-badge"><WifiOff className="w-3 h-3" /> Pay-code mode</Badge>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* New charge */}
          <Card className="p-6" data-testid="pos-new-charge-card">
            <h2 className="text-lg font-semibold mb-4">New charge</h2>
            <form onSubmit={createCharge} className="space-y-4">
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  data-testid="pos-amount-input"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                  className="text-2xl font-mono h-14"
                />
              </div>
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Input
                  data-testid="pos-description-input"
                  placeholder="e.g. Table 4 — lunch"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Merchant reference (optional)</Label>
                <Input
                  data-testid="pos-reference-input"
                  placeholder="auto-generated if blank"
                  value={form.reference}
                  onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))}
                />
              </div>
              <Button type="submit" className="w-full bg-accent hover:bg-accent/90 h-12 text-base" data-testid="pos-create-btn" disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Create charge
              </Button>
            </form>
          </Card>

          {/* Active charge / QR */}
          <Card className="p-6 flex flex-col items-center justify-center text-center" data-testid="pos-qr-card">
            {active ? (
              <>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Scan to pay</p>
                <p className="font-mono text-3xl font-bold text-primary mt-1" data-testid="pos-active-amount">
                  {active.currency} {Number(active.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
                <div className="my-5 p-4 bg-white rounded-xl border shadow-sm" data-testid="pos-qr">
                  <QRCodeSVG value={active.qr_payload || active.pay_code} size={196} level="M" includeMargin />
                </div>
                <p className="text-sm text-muted-foreground">Pay code</p>
                <p className="font-mono text-lg font-semibold tracking-wide" data-testid="pos-pay-code">{active.pay_code}</p>
                {active.description && <p className="text-sm text-muted-foreground mt-1">{active.description}</p>}
                <Badge className="mt-3 bg-slate-100 text-slate-600 border border-slate-200">{active.status}</Badge>
              </>
            ) : (
              <div className="text-muted-foreground py-12">
                <Receipt className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>Create a charge to generate a scannable QR.</p>
              </div>
            )}
          </Card>
        </div>

        {/* Recent charges */}
        <Card className="p-6 mt-6" data-testid="pos-history-card">
          <h2 className="text-lg font-semibold mb-4">Recent charges</h2>
          {charges.length === 0 ? (
            <p className="text-sm text-muted-foreground">No charges yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pos-history-table">
                <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                  <tr>
                    <th className="py-2 pr-4">Time</th>
                    <th className="py-2 pr-4">Pay code</th>
                    <th className="py-2 pr-4 text-right">Amount</th>
                    <th className="py-2 pr-4">Description</th>
                    <th className="py-2 pr-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((c) => (
                    <tr key={c.id} className="border-b border-border/40">
                      <td className="py-2 pr-4 font-mono text-xs">{(c.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{c.pay_code}</td>
                      <td className="py-2 pr-4 text-right font-mono">{c.currency} {Number(c.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="py-2 pr-4">{c.description || '-'}</td>
                      <td className="py-2 pr-4">
                        <Badge className="bg-slate-100 text-slate-600 border border-slate-200">{c.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
